/**
 * Scroll-snap geometry for the desktop mating-results table.
 *
 * The table has a sticky header stack (column headers, optional breed-average
 * row, and the selected sire/dam "primary" row). Each mating block below it is
 * marked with a `[data-snap-point]` attribute. After the user stops scrolling
 * (and on initial layout), we nudge the scroll position so the nearest mating
 * block sits cleanly just beneath that sticky stack, rather than being half
 * hidden behind it. `getMateSnapScrollTop` is the pure geometry calculation;
 * the consuming component (`DesktopMatingTable`) is responsible for actually
 * applying the scroll and for deciding when to call it (scroll-end / layout).
 */

/**
 * When already scrolled near the very bottom, suppress an *upward* snap larger
 * than this. The final mating block can't reach the top of the viewport, so
 * snapping it up would fight the user and feel jumpy; leave the bottom alone.
 */
const SNAP_BOTTOM_GUARD_PX = 40;

/**
 * Scroll a few px *past* the nominal snap target so the previous mate block's
 * bottom border clears underneath the sticky primary row (without this nudge
 * the border was still peeking out just below the sire/dam row).
 */
const SNAP_CLEAR_PREVIOUS_GROUP_PX = 8;

/**
 * Cumulative `offsetTop` of `element` relative to `container`, walking up the
 * `offsetParent` chain. Used instead of `getBoundingClientRect` so the result
 * is a stable scroll coordinate independent of the current scroll position.
 */
function getOffsetWithinScrollContainer(
  element: HTMLElement,
  container: HTMLElement,
): number {
  let top = 0;
  let currentElement: HTMLElement | null = element;
  while (currentElement && currentElement !== container) {
    top += currentElement.offsetTop;
    currentElement = currentElement.offsetParent as HTMLElement | null;
  }
  return top;
}

/**
 * Compute the `scrollTop` that aligns the nearest `[data-snap-point]` mating
 * block just below the sticky header stack, or `null` when no snap should
 * happen.
 *
 * @param container - The scrollable table container.
 * @param snapOffset - Height (px) of the sticky stack the block should clear
 *   (header + breed-average + primary row + gap), measured by the caller.
 * @param currentScrollTop - The container's current `scrollTop`.
 * @returns The target `scrollTop`, or `null` if there are no snap points, the
 *   content isn't scrollable, the nearest block is already aligned (within
 *   1px), or snapping would tug the view up away from the bottom edge.
 */
export function getMateSnapScrollTop(
  container: HTMLElement,
  snapOffset: number,
  currentScrollTop: number,
): number | null {
  const snapPoints =
    container.querySelectorAll<HTMLElement>('[data-snap-point]');
  if (!snapPoints.length) return null;

  const maxScrollTop = Math.max(
    0,
    container.scrollHeight - container.clientHeight,
  );
  if (maxScrollTop < 1) return null;

  let closest: HTMLElement | null = null;
  let closestDistance = Infinity;

  snapPoints.forEach((snapPoint) => {
    const unclampedTargetScrollTop =
      getOffsetWithinScrollContainer(snapPoint, container) -
      snapOffset +
      SNAP_CLEAR_PREVIOUS_GROUP_PX;
    const clampedTargetScrollTop = Math.min(
      Math.max(0, unclampedTargetScrollTop),
      maxScrollTop,
    );
    const distanceFromCurrentScrollTop = Math.abs(
      clampedTargetScrollTop - currentScrollTop,
    );
    if (distanceFromCurrentScrollTop < closestDistance) {
      closestDistance = distanceFromCurrentScrollTop;
      closest = snapPoint;
    }
  });

  if (!closest || closestDistance <= 1) return null;

  const unclampedTargetScrollTop =
    getOffsetWithinScrollContainer(closest, container) -
    snapOffset +
    SNAP_CLEAR_PREVIOUS_GROUP_PX;
  const targetScrollTop = Math.min(
    Math.max(0, unclampedTargetScrollTop),
    maxScrollTop,
  );

  const isNearBottom = currentScrollTop >= maxScrollTop - SNAP_BOTTOM_GUARD_PX;
  if (isNearBottom && targetScrollTop < currentScrollTop - 8) {
    return null;
  }

  if (Math.abs(targetScrollTop - currentScrollTop) <= 1) return null;

  return targetScrollTop;
}
